// File: flight-form.component.ts (standalone, Angular 17+)
// Drop this file into your project and add <app-flight-form> where you want the form.
// Assumes you have Angular Material set up (MatNativeDateModule for datepicker).

import { Component, Inject, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpContext, HttpParams } from '@angular/common/http';
import { map, Observable, of, startWith, switchMap, debounceTime, distinctUntilChanged, tap, combineLatest } from 'rxjs';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AutocompleteDataComponent } from '../../shared/autocomplete.component';
import { Aircraft, Flight, FlightPriceCategory, Instructor, LandingItem, Student, User } from '../../models';
import { AircraftService } from '../../service/aircraft.service';
import { InstructorService } from '../../service/instructor.service';
import { StudentService } from '../../service/student.service';
import { hhMmToMinutes, minutesToHhMm, minutesToTimeHHmm, normalizeHhMmText, timeStrToMinutesHHmm } from '../../utils/time.utils';
import { onFormKeysChange, setupMinutesTextSync } from '../../utils/time-sync.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { bindExternalToggleDisablesControl } from '../../utils/toggle-utils';
import { Router } from '@angular/router';
import { SUCCESS_TOAST } from 'src/app/interceptors/snackbar.tokens';
import { FlightService } from 'src/app/service/flight.service';

// ================= Types matching your Prisma enums =================
// Utility: uppercase ICAO and trim
const ICAO_PATTERN = /^[A-Z0-9]{4}$/;
function toIso(date: Date) {
  return date.toISOString();
}
function combineDateAndTime(date: Date | string, timeHHmm: string): string {
  const d = new Date(date);
  const [hh, mm] = timeHHmm.split(':').map(Number);
  d.setHours(hh ?? 0, mm ?? 0, 0, 0);
  return d.toISOString();
}

@Component({
  selector: 'app-flight-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatCardModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatTooltipModule,
    AutocompleteDataComponent,
  ],
  templateUrl: './flight-detail.component.html',
  styleUrls: ['./flight-detail.component.scss'],
})
export class FlighDetailComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  priceCategories: FlightPriceCategory[] = ['NONE', 'BASIC', 'SOLO_SUP', 'NIGHT', 'FI_I', 'IR'];

  form: FormGroup;
  submitting = signal(false);

  pilotCtrl = new FormControl<string | User>('', { nonNullable: false });
  aircrafts$!: Observable<Aircraft[]>;

  aircraftCtrl = new FormControl<Aircraft | null>(null);
  instructorCtrl = new FormControl<Instructor | null>(null);
  studentCtrl = new FormControl<Student | null>(null);
  allBtInstrumentTimeCtrl = new FormControl<boolean>(false);
  allBtNightTimeCtrl = new FormControl<boolean>(false);

  aircrafts: Aircraft[] = [];
  instructors: Instructor[] = [];
  students: Student[] = [];

  airtime: number;
  blocktime: number;

  constructor(
    private fb: FormBuilder,
    http: HttpClient,
    private aircraftService: AircraftService,
    private instructorService: InstructorService,
    private studentService: StudentService,
    private flightService: FlightService,
    private router: Router
  ) {
    this.form = this.fb.group({
      date: [new Date(), Validators.required],
      aircraftId: ['', Validators.required],
      pilotId: ['', Validators.required],
      instructorId: [null as string | null],
      examinerId: [null as string | null],
      pilotInCommand: [true, Validators.required],
      recurrenceTraining: [false],
      proficiencyCheck: [false],
      priceCategory: ['NONE' as FlightPriceCategory, Validators.required],
      remarks: [''],
      origin: ['', [Validators.required, Validators.pattern(ICAO_PATTERN)]],
      destination: ['', [Validators.required, Validators.pattern(ICAO_PATTERN)]],
      counterBeginMinutes: [0, [Validators.required, Validators.min(0)]],
      counterEndMinutes: [0, [Validators.required, Validators.min(0)]],
      counterBeginText: [''],
      counterEndText: [''],

      // time-only inputs (combined with date on submit)
      blockOffTime: ['', Validators.required],
      takeoffTime: ['', Validators.required],
      landingTime: ['', Validators.required],
      blockOnTime: ['', Validators.required],
      instrumentTime: [0, [Validators.required, Validators.min(0)]],
      nightTime: [0, [Validators.required, Validators.min(0)]],
      instrumentTimeText: [''],
      nightTimeText: [''],
      oilRefilled: [null as null | number],
      fuelRefilled: [null as null | number],
      passengers: [0, [Validators.required, Validators.min(0)]],
      moua: [0, [Validators.required, Validators.min(0)]],
      landings: this.fb.array<FormGroup<LandingGroup>>([this.createLandingRow()]),
    });
  }

  ngOnInit(): void {
    this.aircraftService.getAircrafts().subscribe((a) => (this.aircrafts = a));
    this.instructorService.getInstructors().subscribe((list) => {
      this.instructors = list;
    });
    this.studentService.getStudents?.().subscribe?.((s) => (this.students = s));

    this.aircraftCtrl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged()).subscribe((a) => {
      if (typeof a !== 'string') {
        const currentBeginMinutes = a.currentCounterMinutes ?? a.counterMinutes;
        this.form.patchValue({
          counterBeginMinutes: currentBeginMinutes,
        }),
          { emitEvent: false };
        this.form.patchValue({
          counterEndMinutes: currentBeginMinutes + 45,
        }),
          { emitEvent: false };
      }
    });

    setupMinutesTextSync(
      this.form,
      [
        { minutesKey: 'counterBeginMinutes', textKey: 'counterBeginText' },
        { minutesKey: 'counterEndMinutes', textKey: 'counterEndText' },
        { minutesKey: 'instrumentTime', textKey: 'instrumentTimeText' },
        { minutesKey: 'nightTime', textKey: 'nightTimeText' },
        // ...autres paires
      ],
      { destroyRef: this.destroyRef }
    );

    onFormKeysChange(this.form, ['takeoffTime', 'counterBeginMinutes', 'counterEndMinutes', 'counterBeginText', 'counterEndText'], 50, {
      destroyRef: this.destroyRef,
    }).subscribe(() => this.computeOtherHours());

    onFormKeysChange(this.form, ['blockOnTime', 'blockOffTime'], 50, { destroyRef: this.destroyRef }).subscribe(() => {
      const blockOn = timeStrToMinutesHHmm(this.form.get('blockOnTime')?.value);
      const blockOff = timeStrToMinutesHHmm(this.form.get('blockOffTime')?.value);
      this.blocktime = blockOn - blockOff;
      this.reSyncTimesIfNeeded();
    });

    // Instrument time: désactive le champ texte visible ET met à jour minutes
    bindExternalToggleDisablesControl(
      this.allBtInstrumentTimeCtrl,
      [
        // désactive le champ texte et l’affiche en HH:mm
        {
          ctl: this.form.get('instrumentTimeText')!,
          map: (mins) => minutesToHhMm(mins),
        },
        // garde le contrôle minutes en phase (tu peux choisir de ne PAS le désactiver)
        // this.form.get("instrumentTime")!,
      ],
      () => this.blocktime ?? 0, // compute()
      0, // reset
      this.destroyRef
    );

    // Night time (même idée)
    bindExternalToggleDisablesControl(
      this.allBtNightTimeCtrl,
      [
        {
          ctl: this.form.get('nightTimeText')!,
          map: (mins) => minutesToHhMm(mins),
        },
        // this.form.get("nightTime")!,
      ],
      () => this.blocktime ?? 0,
      0,
      this.destroyRef
    );
  }

  // Landing form group type
  createLandingRow() {
    return this.fb.group<LandingGroup>({
      approachType: new FormControl<'VISUAL' | 'PA_3D' | 'NPA_2D'>('VISUAL', {
        nonNullable: true,
      }),
      isNight: new FormControl<boolean>(false, { nonNullable: true }),
      count: new FormControl<number>(1, {
        nonNullable: true,
        validators: [Validators.min(0)],
      }),
    });
  }
  get landings(): FormArray<FormGroup<LandingGroup>> {
    return this.form.get('landings') as any;
  }
  addLanding() {
    this.landings.push(this.createLandingRow());
  }
  removeLanding(i: number) {
    this.landings.removeAt(i);
  }

  onAircraftSelected(a: Aircraft) {
    this.form.patchValue({ aircraftId: a.id });
  }
  onPilotSelected(s: Student) {
    this.form.patchValue({ pilotId: s.user.id });
  }
  onInstructorSelected(i: Instructor) {
    this.form.patchValue({ instructorId: i.user.id });
  }

  displayFnAircraft = (a?: Aircraft | string) => (typeof a === 'object' ? `${a?.code} — ${a?.model ?? ''}` : a ?? '');
  displayFnUser = (u?: User | string) => (typeof u === 'object' ? `${u?.firstname ?? ''} ${u?.lastname ?? ''} (${u?.username ?? ''})` : u ?? '');

  forceIcaoUpper(controlName: 'origin' | 'destination') {
    const v = (this.form.get(controlName)?.value ?? '') as string;
    const upper = v.toUpperCase().replace(/\s+/g, '');
    this.form.get(controlName)?.setValue(upper, { emitEvent: false });
  }

  reset() {
    this.form.reset({
      date: new Date(),
      pilotInCommand: true,
      priceCategory: 'NONE',
      recurrenceTraining: false,
      proficiencyCheck: false,
      passengers: 0,
      moua: 0,
      counterBeginMinutes: 0,
      counterEndMinutes: 0,
      instrumentTime: 0,
      nightTime: 0,
    });
    this.landings.clear();
    this.addLanding();
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);

    const v = this.form.value;
    const date: Date = v.date as Date;

    const dto: Flight = {
      date: toIso(new Date(date)),
      aircraftId: v.aircraftId!,
      pilotId: v.pilotId!,
      instructorId: v.instructorId ?? null,
      examinerId: v.examinerId ?? null,
      pilotInCommand: !!v.pilotInCommand,
      recurrenceTraining: !!v.recurrenceTraining,
      proficiencyCheck: !!v.proficiencyCheck,
      priceCategory: v.priceCategory as FlightPriceCategory,
      remarks: v.remarks ?? '',
      origin: (v.origin as string).toUpperCase(),
      destination: (v.destination as string).toUpperCase(),
      counterBeginMinutes: Number(v.counterBeginMinutes),
      counterEndMinutes: Number(v.counterEndMinutes),
      blockOff: combineDateAndTime(date, v.blockOffTime as string),
      takeoff: combineDateAndTime(date, v.takeoffTime as string),
      landing: combineDateAndTime(date, v.landingTime as string),
      blockOn: combineDateAndTime(date, v.blockOnTime as string),
      instrumentTime: Number(v.instrumentTime),
      nightTime: Number(v.nightTime),
      oilRefilled: v.oilRefilled == null ? undefined : String(v.oilRefilled),
      fuelRefilled: v.fuelRefilled == null ? undefined : String(v.fuelRefilled),
      passengers: Number(v.passengers ?? 0),
      moua: Number(v.moua ?? 0),
      landings: (this.landings.value as LandingItem[]).map((l) => ({
        approachType: l.approachType,
        isNight: !!l.isNight,
        count: Number(l.count ?? 0),
      })),
    };

    this.flightService
      .createFlight(dto, {
        context: new HttpContext().set(SUCCESS_TOAST, 'Flight successfully saved ✔'),
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.router.navigate(['/flights']);
        },
        error: () => {
          this.submitting.set(false);
          // L’interceptor d’erreurs affiche déjà le toast d’erreur
        },
      });
  }

  private insertColonLastTwo(raw: string | null | undefined): string {
    const s = String(raw ?? '').trim();
    const sign = s.startsWith('-') ? '-' : ''; // optionnel: support du signe
    const digits = s.replace(/[^\d]/g, ''); // garde seulement les chiffres

    if (!digits) return '0:00';
    if (digits.length === 1) return `${sign}0:0${digits}`;

    const hours = digits.slice(0, -2).replace(/^0+/, '') || '0'; // heures sans padding
    const minutes = digits.slice(-2).padStart(2, '0'); // minutes sur 2 digits
    return `${sign}${hours}:${minutes}`;
  }

  formatCounter(controlName) {
    const ctrl = this.form.get(controlName) as FormControl<string>;
    const formatted = this.insertColonLastTwo(ctrl.value);
    ctrl.setValue(formatted, { emitEvent: true }); // met à jour l’UI (et déclenche tes valueChanges)
  }

  computeOtherHours(): void {
    const takeoffStr = this.form.get('takeoffTime')?.value as string | null;
    const beginMinutes = this.form.get('counterBeginMinutes')?.value as number | null;
    const endMinutes = this.form.get('counterEndMinutes')?.value as number | null;

    const t0 = timeStrToMinutesHHmm(takeoffStr ?? '');
    if (t0 == null || beginMinutes == null || endMinutes == null) return;

    const minutesConsumed = endMinutes - beginMinutes; // adapte si compteur peut diminuer

    const landing = t0 + minutesConsumed;
    const blockOff = t0 - 5;
    const blockOn = landing + 5;
    this.airtime = landing - t0;
    this.blocktime = blockOn - blockOff;

    this.form.patchValue(
      {
        landingTime: minutesToTimeHHmm(landing),
        blockOffTime: minutesToTimeHHmm(blockOff),
        blockOnTime: minutesToTimeHHmm(blockOn),
      },
      { emitEvent: false }
    );
    this.reSyncTimesIfNeeded();
  }

  reSyncTimesIfNeeded() {
    if (this.allBtInstrumentTimeCtrl.value) {
      this.form.get('instrumentTime')?.setValue(this.blocktime ?? 0, { emitEvent: true });
    }
    if (this.allBtNightTimeCtrl.value) {
      this.form.get('nightTime')?.setValue(this.blocktime ?? 0, { emitEvent: true });
    }
  }
}

// Typed group for landings
export interface LandingGroup {
  approachType: FormControl<'VISUAL' | 'PA_3D' | 'NPA_2D'>;
  isNight: FormControl<boolean>;
  count: FormControl<number>;
}
